import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Divider,
  Alert,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Paper
} from '@mui/material';
import api, { enhancedApi } from '../api';
import SettingsPageLayout from './settings/SettingsPageLayout';

const AccessPermissions = () => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openAddPermissionSet, setOpenAddPermissionSet] = useState(false);
  const [newPermissionSet, setNewPermissionSet] = useState({
    name: '',
    can_view_invoices: false,
    can_create_invoices: false,
    can_send_invoices_for_payment: false,
    can_access_payments: false,
    can_access_credits: false,
    can_access_expenses: false,
    can_access_statements: false,
    can_access_payment_settings: false,
    access_all_clients: false,
    access_assigned_clients_only: false,
    can_manage_users: false,
    can_access_ai_settings: false
  });
  const [dialogError, setDialogError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Use the multi-tenant API to get permission sets and predefined roles
      const response = await enhancedApi.getPermissionSets();
      
      // Transform the data to match the expected format
      const transformedPermissions = response.data.map(item => ({
        type: item.type,
        value: item.id,
        label: item.name,
        user_count: item.user_count,
        is_predefined: item.is_predefined,
        role_value: item.role_value,
        permission_set_data: item.permission_set_data,
        ...item
      }));
      
      setPermissions(transformedPermissions);
      setLoading(false);
    } catch {
      setError('Failed to load permission sets. Please try again.');
      setLoading(false);
    }
  };

  const handleOpenAddPermissionSet = () => {
    setOpenAddPermissionSet(true);
    setDialogError('');
  };

  const handleCloseAddPermissionSet = () => {
    setOpenAddPermissionSet(false);
    setNewPermissionSet({
      name: '',
      can_view_invoices: false,
      can_create_invoices: false,
      can_send_invoices_for_payment: false,
      can_access_payments: false,
      can_access_credits: false,
      can_access_expenses: false,
      can_access_statements: false,
      can_access_payment_settings: false,
      access_all_clients: false,
      access_assigned_clients_only: false,
      can_manage_users: false,
      can_access_ai_settings: false
    });
    setDialogError('');
    setSaving(false);
  };

  const handlePermissionSetFieldChange = (field, value) => {
    setNewPermissionSet(prev => ({ ...prev, [field]: value }));
    setDialogError('');
  };

  const handleClientAccessChange = (value) => {
    setNewPermissionSet(prev => ({
      ...prev,
      access_all_clients: value === 'all',
      access_assigned_clients_only: value === 'assigned'
    }));
    setDialogError('');
  };

  const handleSavePermissionSet = async () => {
    if (!newPermissionSet.name) {
      setDialogError('Permission set name is required');
      return;
    }

    if (!newPermissionSet.access_all_clients && !newPermissionSet.access_assigned_clients_only) {
      setDialogError('Please select a client access option');
      return;
    }

    setSaving(true);
    setDialogError('');
    
    try {
      const userId = localStorage.getItem('userId');
      const payload = {
        ...newPermissionSet,
        created_by: userId
      };

      // Use the multi-tenant API to create permission set
      await enhancedApi.createPermissionSet(payload);
      
      fetchPermissions(); // Refresh the permissions list
      handleCloseAddPermissionSet();
    } catch (error) {
      if (error.response?.data?.name) {
        setDialogError('A permission set with this name already exists');
      } else {
        setDialogError('Failed to save permission set');
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePermissionClick = (permission) => {
    // Placeholder for future implementation based on your guidance
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>Access and Permissions</Typography>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <SettingsPageLayout title="Access and Permissions" description="Manage permission sets and roles for your team." maxWidth={900}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Button
          variant="contained"
          onClick={handleOpenAddPermissionSet}
          aria-label="Add Permission Set"
          sx={{ fontWeight: 600, textTransform: 'none', fontSize: 14 }}
        >
          Add Permission Set
        </Button>
      </Box>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      {/* Permissions Table */}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', borderColor: 'divider', mb: 4 }}>
        <Box sx={{ px: 3, py: 2, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 13, letterSpacing: '0.02em', color: 'text.secondary' }}>Permissions Overview</Typography>
        </Box>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: 13, letterSpacing: '0.02em', color: 'text.secondary', py: 1.5 }}>Permission Set / Role</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 13, letterSpacing: '0.02em', color: 'text.secondary', py: 1.5 }}>Number of Users</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {permissions.length > 0 ? (
              permissions.map((perm, index) => (
                <TableRow
                  key={index}
                  hover
                  onClick={() => handlePermissionClick(perm)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    {perm.is_predefined ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography color="primary" sx={{ fontWeight: 'bold' }}>
                          {perm.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          (Predefined Role)
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography color="secondary" sx={{ fontWeight: 'bold' }}>
                          {perm.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          (Custom Permission Set)
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{perm.user_count}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography variant="body1" align="center">No permission sets found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Add Permission Set Dialog */}
      <Dialog open={openAddPermissionSet} onClose={handleCloseAddPermissionSet} maxWidth="sm" fullWidth>
        <DialogTitle>Add Permission Set</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {dialogError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {dialogError}
              </Alert>
            )}
            <TextField
              label="Permission Set Name"
              value={newPermissionSet.name}
              onChange={(e) => handlePermissionSetFieldChange('name', e.target.value)}
              fullWidth
              margin="normal"
              required
              disabled={saving}
            />
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Billing Permissions
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={newPermissionSet.can_view_invoices}
                  onChange={(e) => handlePermissionSetFieldChange('can_view_invoices', e.target.checked)}
                  disabled={saving}
                />
              }
              label="View Invoices"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newPermissionSet.can_create_invoices}
                  onChange={(e) => handlePermissionSetFieldChange('can_create_invoices', e.target.checked)}
                  disabled={saving}
                />
              }
              label="Create Invoices"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newPermissionSet.can_send_invoices_for_payment}
                  onChange={(e) => handlePermissionSetFieldChange('can_send_invoices_for_payment', e.target.checked)}
                  disabled={saving}
                />
              }
              label="Send Invoices for Payment"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newPermissionSet.can_access_payments}
                  onChange={(e) => handlePermissionSetFieldChange('can_access_payments', e.target.checked)}
                  disabled={saving}
                />
              }
              label="Access Payments Section"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newPermissionSet.can_access_credits}
                  onChange={(e) => handlePermissionSetFieldChange('can_access_credits', e.target.checked)}
                  disabled={saving}
                />
              }
              label="Access Credits Section"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newPermissionSet.can_access_expenses}
                  onChange={(e) => handlePermissionSetFieldChange('can_access_expenses', e.target.checked)}
                  disabled={saving}
                />
              }
              label="Access Expenses Section"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newPermissionSet.can_access_statements}
                  onChange={(e) => handlePermissionSetFieldChange('can_access_statements', e.target.checked)}
                  disabled={saving}
                />
              }
              label="Access Statements Section"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newPermissionSet.can_access_payment_settings}
                  onChange={(e) => handlePermissionSetFieldChange('can_access_payment_settings', e.target.checked)}
                  disabled={saving}
                />
              }
              label="Access Payment Settings Section"
            />
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" gutterBottom>
              Client Access Permissions
            </Typography>
            <FormControl component="fieldset">
              <FormLabel component="legend">Client Access</FormLabel>
              <RadioGroup
                value={newPermissionSet.access_all_clients ? 'all' : newPermissionSet.access_assigned_clients_only ? 'assigned' : ''}
                onChange={(e) => handleClientAccessChange(e.target.value)}
              >
                <FormControlLabel
                  value="all"
                  control={<Radio disabled={saving} />}
                  label="Access All Clients"
                />
                <FormControlLabel
                  value="assigned"
                  control={<Radio disabled={saving} />}
                  label="Access Assigned Clients Only"
                />
              </RadioGroup>
            </FormControl>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" gutterBottom>
              Other Permissions
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={newPermissionSet.can_manage_users}
                  onChange={(e) => handlePermissionSetFieldChange('can_manage_users', e.target.checked)}
                  disabled={saving}
                />
              }
              label="Manage Users"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newPermissionSet.can_access_ai_settings}
                  onChange={(e) => handlePermissionSetFieldChange('can_access_ai_settings', e.target.checked)}
                  disabled={saving}
                />
              }
              label="Access AI Settings"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddPermissionSet} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSavePermissionSet} 
            variant="contained" 
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Permission Set'}
          </Button>
        </DialogActions>
      </Dialog>
    </SettingsPageLayout>
  );
};

export default AccessPermissions;